import React, { createContext, useContext, useState, ReactNode } from 'react';
import apiClient from '../axios';
import { User } from '../types/User.type';

// 인증 컨텍스트의 상태를 정의합니다.
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (accessToken: string) => void;
  logout: () => void;
}

// 기본 인증 컨텍스트 값 (초기 상태)
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider 컴포넌트: 인증 상태를 관리하고 자식 컴포넌트에 제공합니다.
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * 현재 로그인된 사용자 정보를 백엔드에서 가져오는 비동기 함수
   * localStorage의 accessToken을 통해 인증 상태를 확인하고 사용자 정보를 설정합니다.
   */
  const fetchUser = React.useCallback(async () => {
    const jwtToken = localStorage.getItem('accessToken');

    if (!jwtToken) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiClient.get<User>('/api/user/me');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error(error);
      // Token invalid or expired on server-side
      localStorage.removeItem('accessToken');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * 로그인 처리 함수
   * @param accessToken OAuth 응답에서 받은 JWT 토큰
   */
  const login = React.useCallback(async (accessToken: string) => {
    console.log('AuthContext, accessToken', accessToken);
    localStorage.setItem('accessToken', accessToken);

    await fetchUser();
  }, [fetchUser]);

  /**
   * 로그아웃 처리 함수
   * 로컬 스토리지에서 토큰을 제거하고, 사용자 상태를 초기화합니다.
   */
  const logout = React.useCallback(() => {
    localStorage.removeItem('accessToken');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  }, []);

  // 어드민 권한 확인 (role이 'ADMIN'인 경우)
  const isAdmin = React.useMemo(() => {
    return user?.role === 'ADMIN';
  }, [user]);

  const value = React.useMemo(
    () => ({ user, isAuthenticated, isAdmin, isLoading, login, logout }),
    [user, isAuthenticated, isAdmin, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// AuthContext를 쉽게 사용할 수 있도록 커스텀 훅을 제공합니다.
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
